const STORAGE_KEY = 'moky-cookie-choice';
const OPEN_EVENT = 'moky:open-cookies';

/**
 * @returns {string | null}
 */
function readChoice() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @param {'accepted' | 'essentials'} value
 */
function writeChoice(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage can be blocked; the UI still closes for this visit.
  }
}

/**
 * @returns {Promise<typeof window.Shopify.customerPrivacy | null>}
 */
function loadPrivacyApi() {
  return new Promise((resolve) => {
    const existing = window.Shopify?.customerPrivacy;
    if (existing?.setTrackingConsent) {
      resolve(existing);
      return;
    }

    const loader = window.Shopify?.loadFeatures;
    if (typeof loader !== 'function') {
      resolve(null);
      return;
    }

    loader([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        resolve(null);
        return;
      }

      resolve(window.Shopify?.customerPrivacy ?? null);
    });
  });
}

/**
 * @param {boolean} accepted
 */
async function setShopifyConsent(accepted) {
  const api = await loadPrivacyApi();
  if (!api?.setTrackingConsent) return;

  await new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    try {
      api.setTrackingConsent(
        {
          analytics: accepted,
          marketing: accepted,
          preferences: accepted,
          sale_of_data: accepted,
        },
        done
      );
    } catch {
      done();
    }

    window.setTimeout(done, 1200);
  });
}

/**
 * Small, non-blocking cookie bar at the bottom of the screen.
 * Uses dialog.show() (not showModal), so there is no backdrop and the
 * page behind stays usable. The X accepts, like the Accept button.
 */
class CookieConsentBar {
  /**
   * @param {HTMLDialogElement} dialog
   */
  constructor(dialog) {
    this.dialog = dialog;
    dialog.querySelector('[data-cookie-accept]')?.addEventListener('click', () => this.#choose(true));
    dialog.querySelector('[data-cookie-close]')?.addEventListener('click', () => this.#choose(true));
    dialog.querySelector('[data-cookie-essentials]')?.addEventListener('click', () => this.#choose(false));
  }

  init() {
    document.addEventListener(OPEN_EVENT, () => this.open());
    document.addEventListener('click', async (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-open-cookies]') : null;
      if (!trigger) return;

      event.preventDefault();
      const host = trigger.closest('dialog-component');
      if (host && typeof host.closeDialog === 'function') {
        await host.closeDialog();
      }
      this.open();
    });

    if (readChoice()) return;

    // Let the page finish its first paint before the bar slides in.
    const show = () => window.setTimeout(() => this.open(), 400);
    if (document.readyState === 'complete') show();
    else window.addEventListener('load', show, { once: true });
  }

  open() {
    this.dialog.classList.remove('is-closing');
    this.dialog.removeAttribute('hidden');
    if (!this.dialog.open) {
      if (typeof this.dialog.show === 'function') this.dialog.show();
      else this.dialog.setAttribute('open', '');
    }
    this.dialog.classList.add('is-open');
  }

  /**
   * @param {boolean} accepted
   */
  #choose(accepted) {
    writeChoice(accepted ? 'accepted' : 'essentials');
    this.#close();
    setShopifyConsent(accepted);
  }

  #close() {
    this.dialog.classList.remove('is-open');
    this.dialog.classList.add('is-closing');

    const finish = () => {
      if (this.dialog.open && typeof this.dialog.close === 'function') this.dialog.close();
      this.dialog.removeAttribute('open');
      this.dialog.setAttribute('hidden', '');
      this.dialog.classList.remove('is-closing');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    window.setTimeout(finish, 260);
  }
}

const root = document.getElementById('CookieConsent');
if (root instanceof HTMLDialogElement) {
  new CookieConsentBar(root).init();
}
